import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { DocumentDetailClassAttributes } from "@albatrosdeveloper/ave-models-package";

const apiUrl = env.API_DOCUMENT_URL;
const moduleName = "document_detail_class";

@Injectable({
  providedIn: "root",
})
export class DocumentLineClassService extends AbstractService<
  DocumentDetailClassAttributes,
  DocumentDetailClassAttributes,
  DocumentDetailClassAttributes
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<DocumentDetailClassAttributes>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResult<DocumentDetailClassAttributes>> {
    const hasSearch = !!options.search;

     /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<DocumentDetailClassAttributes>[] => {
      const deletedFilter: FilterFn<DocumentDetailClassAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
        ? andWhere("companies.id" as keyof DocumentDetailClassAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),
        dataToFilter?.documentTypeLineId && dataToFilter?.documentTypeLineId.length > 0
        ? andWhere("documentDetailType.code" as keyof DocumentDetailClassAttributes, Ops.in(...dataToFilter.documentTypeLineId))
        : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
        ? andWhere("statusId", Ops.in(...dataToFilter.status))
        : noop(),
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<DocumentDetailClassAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<DocumentDetailClassAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========
    const query = buildQuery<DocumentDetailClassAttributes>(
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      
      orderBy(
        options.sort ?? "name",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount`;
    return this.http.post<IPaginationResult<DocumentDetailClassAttributes>>(API_URL, {
      filter: query
    });
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<DocumentDetailClassAttributes[]>> {
    const query = buildQuery<DocumentDetailClassAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["externalKeys", "code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DocumentDetailClassAttributes[]>>(API_URL);
  }
}